/**
 * Image upload for the shared rich-text editor.
 *
 * The shared editor (`RichTextEditor` from `@ampersandbrands/ui`) never uploads
 * anything itself. It hands the chosen file to `onInsertImage` and embeds
 * whatever src comes back, so each app decides where its images live. This is
 * Cockpit's answer, lifted unchanged from the local editor this replaced.
 *
 * Two steps: ask the api for a slot, then PUT the bytes straight to S3 with the
 * exact `Content-Type` we declared, because that header is part of the
 * signature. Bytes never enter the body HTML.
 *
 * The returned src is absolute so it renders immediately while editing. The api
 * sanitizes on write and normalizes it back to the relative `/v1/core/images/{id}`
 * form, which is what gets stored. See `richTextHtml.ts` for the read side.
 */

import { API_BASE } from './apiBase';
import { apiPost } from './api';

/** What the api accepts, mirrored so a bad file is rejected with a clear message. */
export const ALLOWED_EDITOR_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

/** Matches the api's practical ceiling; a bigger paste gets a message, not a failure. */
export const MAX_EDITOR_IMAGE_BYTES = 10 * 1024 * 1024;

export async function uploadEditorImage(file: File): Promise<{ src: string }> {
  if (file.type && !ALLOWED_EDITOR_IMAGE_TYPES.includes(file.type)) {
    throw new Error(`${file.name || 'That image'} is not a PNG, JPEG, GIF, or WebP.`);
  }
  if (file.size > MAX_EDITOR_IMAGE_BYTES) {
    throw new Error(`${file.name || 'That image'} is larger than 10MB.`);
  }
  const { src, upload_url } = await apiPost<{ id: number; src: string; upload_url: string }>(
    `${API_BASE}/v1/core/images`,
    { filename: file.name || 'pasted-image.png', content_type: file.type },
  );
  const put = await fetch(upload_url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });
  if (!put.ok) throw new Error(`upload failed (${put.status})`);
  return { src: `${API_BASE}${src}` };
}
